import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getQuestionsByCategory, submitResponses } from '../services/api';
import logo from '../assets/logo.png';

const Questionnaire = () => {
  const { diagnosticId } = useParams();
  const navigate = useNavigate();

  const [categoriesWithQuestions, setCategoriesWithQuestions] = useState([]);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showValidationError, setShowValidationError] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);

  useEffect(() => {
    fetchQuestionsData();
  }, []);

  const fetchQuestionsData = async () => {
    try {
      setLoading(true);
      const data = await getQuestionsByCategory();

      const groupedByCategory = data.reduce((acc, question) => {
        const categoryId = question.category;
        const categoryName = question.category_name;

        let categoryGroup = acc.find((g) => g.category.id === categoryId);

        if (!categoryGroup) {
          categoryGroup = {
            category: { id: categoryId, name: categoryName },
            questions: [],
          };
          acc.push(categoryGroup);
        }

        categoryGroup.questions.push(question);
        return acc;
      }, []);

      groupedByCategory.sort((a, b) => a.category.id - b.category.id);
      setCategoriesWithQuestions(groupedByCategory);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setError('Failed to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentCategory = categoriesWithQuestions[currentCategoryIndex];
  const questions = currentCategory?.questions || [];
  const totalCategories = categoriesWithQuestions.length;
  const isFirstCategory = currentCategoryIndex === 0;
  const isLastCategory = currentCategoryIndex === totalCategories - 1;

  const totalQuestions = categoriesWithQuestions.reduce(
    (sum, cat) => sum + (cat.questions?.length || 0),
    0
  );
  const answeredQuestions = Object.keys(responses).length;
  const progressPercent =
    totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

  const isQuestionAnswered = (questionId) => {
    const value = responses[questionId];
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  const isCategoryComplete = () => questions.every((q) => isQuestionAnswered(q.id));
  const isQuestionnaireComplete = () =>
    categoriesWithQuestions.every((cat) =>
      cat.questions?.every((q) => isQuestionAnswered(q.id))
    );
  const getUnansweredInCategory = () => questions.filter((q) => !isQuestionAnswered(q.id));

  const answeredInCategory = questions.filter((q) => isQuestionAnswered(q.id)).length;
  const totalInCategory = questions.length;

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
    setShowValidationError(false);
  };

  const handleMultiSelectChange = (questionId, optionText, checked) => {
    setResponses((prev) => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, optionText] };
      } else {
        return { ...prev, [questionId]: current.filter((v) => v !== optionText) };
      }
    });
    setShowValidationError(false);
  };

  const handleNext = () => {
    if (!isCategoryComplete()) {
      const unanswered = getUnansweredInCategory();
      setUnansweredQuestions(unanswered);
      setShowValidationError(true);

      if (unanswered.length > 0) {
        const element = document.getElementById(`question-${unanswered[0].id}`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!isLastCategory) {
      setCurrentCategoryIndex((prev) => prev + 1);
      setShowValidationError(false);
      setUnansweredQuestions([]);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (!isFirstCategory) {
      setCurrentCategoryIndex((prev) => prev - 1);
      setShowValidationError(false);
      setUnansweredQuestions([]);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!isQuestionnaireComplete()) {
      setShowValidationError(true);
      setError('Please complete all questions before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formattedResponses = Object.entries(responses).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        answer: Array.isArray(answer) ? answer.join(', ') : String(answer),
      }));

      await submitResponses(diagnosticId, formattedResponses);
      navigate(`/report/${diagnosticId}`);
    } catch (err) {
      console.error('Failed to submit:', err);
      setError('Failed to submit responses. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isQuestionHighlighted = (questionId) => {
    return showValidationError && unansweredQuestions.some((q) => q.id === questionId);
  };

  const renderQuestion = (question) => {
    const value = responses[question.id];

    switch (question.question_type) {
      case 'yes_no':
        return (
          <div className="flex gap-4">
            {['Yes', 'No'].map((option) => (
              <label key={option} className="flex items-center gap-3 cursor-pointer group">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    value === option
                      ? 'border-[#9fc6ff] bg-[#9fc6ff]'
                      : 'border-neutral-600 group-hover:border-[#9fc6ff]'
                  }`}
                >
                  {value === option && <div className="w-2 h-2 rounded-full bg-[#05070d]"></div>}
                </div>
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                  className="sr-only"
                />
                <span className="text-neutral-300">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'scale_1_5':
        return (
          <div className="flex flex-wrap gap-3">
            {[1, 2, 3, 4, 5].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleResponseChange(question.id, num)}
                className={`w-14 h-14 font-serif font-bold text-xl transition-all ${
                  value === num
                    ? 'bg-[#9fc6ff] text-[#05070d]'
                    : 'bg-[#0b1220] text-neutral-400 border border-white/10 hover:border-[#9fc6ff] hover:text-[#9fc6ff]'
                }`}
              >
                {num}
              </button>
            ))}
            <span className="ml-4 text-sm text-neutral-500 self-center">
              (1 = Poor, 5 = Excellent)
            </span>
          </div>
        );

      case 'mcq':
        return (
          <div className="space-y-2">
            {question.options?.map((option, idx) => {
              const optionText = option.text || option.label || option;
              return (
                <label
                  key={idx}
                  className="flex items-center gap-3 cursor-pointer p-3 bg-white/5 border border-white/10 hover:border-[#9fc6ff]/50 transition-colors group"
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      value === optionText
                        ? 'border-[#9fc6ff] bg-[#9fc6ff]'
                        : 'border-neutral-600 group-hover:border-[#9fc6ff]'
                    }`}
                  >
                    {value === optionText && <div className="w-2 h-2 rounded-full bg-[#05070d]"></div>}
                  </div>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={optionText}
                    checked={value === optionText}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-neutral-300">{optionText}</span>
                </label>
              );
            })}
          </div>
        );

      case 'multi_select':
        const selectedValues = value || [];
        return (
          <div className="space-y-2">
            {question.options?.map((option, idx) => {
              const optionText = option.text || option.label || option;
              const isSelected = selectedValues.includes(optionText);
              return (
                <label
                  key={idx}
                  className="flex items-center gap-3 cursor-pointer p-3 bg-white/5 border border-white/10 hover:border-[#9fc6ff]/50 transition-colors group"
                >
                  <div
                    className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-[#9fc6ff] bg-[#9fc6ff]'
                        : 'border-neutral-600 group-hover:border-[#9fc6ff]'
                    }`}
                  >
                    {isSelected && (
                      <svg className="w-3 h-3 text-[#05070d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) =>
                      handleMultiSelectChange(question.id, optionText, e.target.checked)
                    }
                    className="sr-only"
                  />
                  <span className="text-neutral-300">{optionText}</span>
                </label>
              );
            })}
            <p className="text-sm text-neutral-500 mt-2">Select all that apply</p>
          </div>
        );

      case 'numeric':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-40 px-4 py-3 bg-[#0b1220] border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-[#9fc6ff] transition-colors"
            placeholder="Enter number"
            min="0"
          />
        );

      case 'free_text':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full px-4 py-3 bg-[#0b1220] border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-[#9fc6ff] transition-colors resize-none"
            rows={3}
            placeholder="Enter your response..."
          />
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="w-full px-4 py-3 bg-[#0b1220] border border-white/10 text-white placeholder-neutral-500 focus:outline-none focus:border-[#9fc6ff] transition-colors"
            placeholder="Enter your response"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="text-center relative">
          <div className="w-16 h-16 border-4 border-[#9fc6ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (error && categoriesWithQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="text-center relative">
          <p className="text-red-400 mb-6">{error}</p>
          <button
            onClick={fetchQuestionsData}
            className="px-6 py-3 bg-[#9fc6ff] text-[#05070d] font-medium hover:opacity-90 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05070d] text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Decorative glow */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Progress Header */}
      <div className="bg-[#05070d]/95 backdrop-blur-md border-b border-white/10 sticky top-0 z-20 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center mb-3 gap-4">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-[#9fc6ff] hover:text-white text-sm transition-colors"
            >
              <img
                src={logo}
                alt="RAL Real Advisor"
                className="w-6 h-6 object-contain"
              />
              <span>Save & Exit</span>
            </Link>
            <span className="text-neutral-400 text-sm">
              Category {currentCategoryIndex + 1} of {totalCategories}
            </span>
            <span className="text-white font-medium">{progressPercent}% Complete</span>
          </div>
          <div className="w-full bg-white/10 h-1">
            <div
              className="bg-gradient-to-r from-[#7eaee8] to-[#9fc6ff] h-1 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 relative">
        {/* Category Header */}
        <div className="mb-10">
          <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-2 block">
            Section {currentCategoryIndex + 1}
          </span>
          <h1 className="text-3xl font-serif font-bold text-white mb-2">
            {currentCategory?.category?.name}
          </h1>
          <p className="text-neutral-400">
            {answeredInCategory} of {totalInCategory} questions answered
          </p>

          <div className="mt-4 w-full bg-white/10 h-1">
            <div
              className={`h-1 transition-all duration-300 ${
                answeredInCategory === totalInCategory ? 'bg-green-500' : 'bg-[#7eaee8]'
              }`}
              style={{
                width: `${totalInCategory > 0 ? (answeredInCategory / totalInCategory) * 100 : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Validation Error Banner */}
        {showValidationError && (
          <div className="mb-8 p-4 bg-red-900/30 border border-red-700/50">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-medium text-red-400">Please answer all questions</p>
                <p className="text-sm text-red-400/70 mt-1">
                  {unansweredQuestions.length} question
                  {unansweredQuestions.length !== 1 ? 's' : ''} remaining in this section
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6">
          {questions.map((question, index) => {
            const isAnswered = isQuestionAnswered(question.id);
            const isHighlighted = isQuestionHighlighted(question.id);

            return (
              <div
                key={question.id}
                id={`question-${question.id}`}
                className={`bg-white/5 backdrop-blur border p-6 transition-all ${
                  isHighlighted
                    ? 'border-red-500'
                    : isAnswered
                    ? 'border-green-600/50'
                    : 'border-white/10'
                }`}
              >
                <div className="flex gap-4">
                  <span
                    className={`flex-shrink-0 w-10 h-10 flex items-center justify-center text-sm font-bold ${
                      isAnswered
                        ? 'bg-green-600 text-white'
                        : isHighlighted
                        ? 'bg-red-600 text-white'
                        : 'bg-white/10 text-neutral-300'
                    }`}
                  >
                    {isAnswered ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </span>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-white mb-4">
                      {question.text}
                      {!isAnswered && <span className="text-red-400 ml-1">*</span>}
                    </h3>
                    {renderQuestion(question)}

                    {isHighlighted && (
                      <p className="mt-3 text-sm text-red-400">
                        Please answer this question
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-900/30 border border-red-700/50">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-12 flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={isFirstCategory}
            className={`px-6 py-3 border font-medium transition-all ${
              isFirstCategory
                ? 'border-white/10 text-neutral-600 cursor-not-allowed'
                : 'border-white/10 text-neutral-300 hover:border-[#9fc6ff] hover:text-[#9fc6ff]'
            }`}
          >
            ← Back
          </button>

          <div className="flex items-center gap-4">
            {isCategoryComplete() && (
              <span className="text-green-400 text-sm flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Section complete
              </span>
            )}

            {isLastCategory ? (
              <button
                onClick={handleSubmit}
                disabled={submitting || !isQuestionnaireComplete()}
                className={`px-8 py-3 font-medium transition-all ${
                  isQuestionnaireComplete()
                    ? 'bg-[#9fc6ff] text-[#05070d] hover:opacity-90'
                    : 'bg-white/10 text-neutral-500 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Submitting...' : 'Submit Diagnostic'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-8 py-3 bg-[#9fc6ff] text-[#05070d] font-medium hover:opacity-90 transition-all"
              >
                Next →
              </button>
            )}
          </div>
        </div>

        {/* Overall Progress Footer */}
        <div className="mt-12 p-6 bg-white/5 backdrop-blur border border-white/10">
          <div className="flex justify-between text-sm text-neutral-400 mb-3">
            <span>Overall Progress</span>
            <span>
              {answeredQuestions} of {totalQuestions} questions answered
            </span>
          </div>
          <div className="w-full bg-white/10 h-2">
            <div
              className={`h-2 transition-all duration-300 ${
                isQuestionnaireComplete() ? 'bg-green-500' : 'bg-[#7eaee8]'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {isQuestionnaireComplete() && (
            <p className="mt-4 text-green-400 text-sm text-center">
              ✓ All questions completed! You can now submit your diagnostic.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;